/**
 * PropertiesPage — route `/properties` (PMS portal).
 *
 * SCRUM-169 (P1.1) — Properties list, by-property view wired + by-unit
 * view shell. Replaces the SCRUM-168 P0 NotConnectedYet placeholder.
 *
 * Source of truth: `~/Desktop/design_handoff_properties/
 * PROPERTIES_LIST_SPEC.md`. Highlights:
 *
 *   - Header chrome (eyebrow "Inventory" + H1 + counter subtitle)
 *   - View toggle (By property / All units) → URL `?view=units`
 *   - Filter row (search + Status FilterSel + Type FilterSel by-property only)
 *   - By-property table — wired to `useProperties()` over the BE list endpoint
 *   - By-unit view — `<NotConnectedYet>` because the BE has no Unit entity
 *     (Property IS the unit today; see SCRUM-167 epic shell/wired matrix)
 *   - States: empty (zero properties / filtered to zero), loading skeleton,
 *     error retry, forbidden permission-denied panel
 *
 * Permission gate is upstream (router.tsx — `property.read`); this page
 * doesn't re-check. If `useProperties()` returns `'forbidden'` the BE
 * has revoked the slug since `/auth/me` cached — render the panel.
 *
 * Out of scope for this commit (deferred to SCRUM-182 mobile sweep):
 *   - Sticky view-toggle on mobile
 *   - Mobile card layout (rendered desktop-only for now)
 */

import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge, Button, Card, Icon } from '@sparta/ui-kit';
import { useSrAnnouncement } from '../../../shared/hooks/useSrAnnouncement.js';
import { useProperties } from '../api/use-properties-list.js';
import type {
  PropertiesListInput,
  PropertyType,
  PropertyView,
} from '../api/types.js';
import { TypeIcon } from '../_components/TypeIcon.js';
import { UnitStatus } from '../_components/UnitStatus.js';
import { FilterSel } from '../_components/FilterSel.js';
import { NotConnectedYet } from '../../../entities/not-connected-yet/index.js';

type ViewMode = 'properties' | 'units';
type StatusFilter = 'all' | 'bookable' | 'non-bookable';
type TypeFilter = 'all' | PropertyType;

const STATUS_OPTIONS = [
  { id: 'all' as const, label: 'All statuses' },
  { id: 'bookable' as const, label: 'Bookable' },
  { id: 'non-bookable' as const, label: 'Non-bookable' },
];

const TYPE_OPTIONS = [
  { id: 'all' as const, label: 'All types' },
  { id: 'Villa' as const, label: 'Villa' },
  { id: 'Condominium' as const, label: 'Condominium' },
  { id: 'Home' as const, label: 'Home' },
  { id: 'Cabin' as const, label: 'Cabin' },
];

/** Debounce helper kept local — small enough that the kit-promotion ladder doesn't fire. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

export function PropertiesPage(): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view');
  const view: ViewMode = viewParam === 'units' ? 'units' : 'properties';

  const [query, setQuery] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = React.useState<TypeFilter>('all');

  // Debounce 180ms per PROPERTIES_LIST_SPEC §6 + IMPLEMENTATION_CHECKLIST.
  const debouncedQuery = useDebouncedValue(query, 180);

  const listInput: PropertiesListInput = React.useMemo(
    () => ({
      q: debouncedQuery,
      status: statusFilter,
      // Type filter is intentionally property-view-only; for units view we
      // pass `all` so the BE fetch isn't filtered by type (the units view
      // is shell anyway).
      type: view === 'properties' ? typeFilter : 'all',
    }),
    [debouncedQuery, statusFilter, typeFilter, view],
  );

  const result = useProperties(listInput);

  // Screen-reader announcements per the bundle's ACCESSIBILITY.md §6.
  //
  // Filter / search result-count changes are announced by the visible
  // `aria-live="polite"` counter on the filter row ("{N} of {M}") —
  // that element IS the canonical announcement surface, so the hook
  // does NOT duplicate it (Codex round-3 caught the duplicate path).
  // We keep the hook for view-toggle announcements, which don't have
  // a visible live region of their own.
  const sr = useSrAnnouncement();
  const previousView = React.useRef<ViewMode>(view);
  React.useEffect(() => {
    if (previousView.current !== view) {
      sr.polite(view === 'units' ? 'All units view active' : 'By property view active');
      previousView.current = view;
    }
  }, [view, sr]);

  const handleViewChange = (next: ViewMode): void => {
    if (next === 'units') {
      setSearchParams((prev) => {
        const out = new URLSearchParams(prev);
        out.set('view', 'units');
        return out;
      });
    } else {
      setSearchParams((prev) => {
        const out = new URLSearchParams(prev);
        out.delete('view');
        return out;
      });
    }
  };

  const filteredCount = result.status === 'ready' ? result.data.length : 0;
  const total = result.status === 'ready' ? result.total : 0;
  // Subtitle counter — bundle calls for "{N} properties · {N} units · {N}
  // bookable". Units = N today (Property IS the unit); bookable = count of
  // `op === active && av === bookable`.
  const bookableTotal =
    result.status === 'ready'
      ? result.data.filter(
          (p) => p.status.op === 'active' && p.status.av === 'bookable',
        ).length
      : 0;

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-12 sm:py-8">
      {sr.regions}
      {/* Eyebrow + H1 + subtitle + primary actions */}
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p
            className="text-xs font-bold uppercase mb-1"
            style={{
              color: 'var(--fg-tertiary)',
              letterSpacing: '0.12em',
            }}
          >
            Inventory
          </p>
          <h1
            className="text-2xl font-semibold m-0"
            style={{
              color: 'var(--fg-primary)',
              letterSpacing: '-0.025em',
            }}
          >
            Properties
          </h1>
          <p
            className="mt-1.5 text-sm m-0"
            style={{ color: 'var(--fg-secondary)' }}
          >
            {result.status === 'ready'
              ? `${total} ${total === 1 ? 'property' : 'properties'} · ${total} ${total === 1 ? 'unit' : 'units'} · ${bookableTotal} bookable`
              : ' '}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => navigate('/properties/import')}
          >
            <Icon name="arrowUp" size={16} aria-hidden />
            <span className="ml-2">Import CSV</span>
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate('/properties/new')}
          >
            <Icon name="plus" size={16} aria-hidden />
            <span className="ml-2">New property</span>
          </Button>
        </div>
      </header>

      <ViewToggleTablist
        view={view}
        onChange={handleViewChange}
      />

      {/* Filter row — search + status + (type, by-property only) */}
      <Card padding="none" className="mb-4">
        <div className="flex flex-wrap items-center gap-2.5 p-4">
          <div
            className="inline-flex items-center gap-2 h-9 px-3.5 rounded-full flex-1 max-w-sm min-w-60"
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-default)',
            }}
          >
            <Icon
              name="search"
              size={14}
              aria-hidden
              style={{ color: 'var(--fg-tertiary)' }}
            />
            <input
              type="search"
              aria-label={
                view === 'properties'
                  ? 'Search by name or city'
                  : 'Search units, properties'
              }
              placeholder={
                view === 'properties'
                  ? 'Search by name or city'
                  : 'Search units, properties'
              }
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 border-none outline-none bg-transparent text-sm"
              style={{
                font: 'inherit',
                color: 'var(--fg-primary)',
              }}
            />
          </div>
          <FilterSel<StatusFilter>
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={STATUS_OPTIONS}
          />
          {view === 'properties' ? (
            <FilterSel<TypeFilter>
              label="Type"
              value={typeFilter}
              onChange={setTypeFilter}
              options={TYPE_OPTIONS}
            />
          ) : null}
          <span
            className="ml-auto text-sm"
            style={{ color: 'var(--fg-tertiary)' }}
            aria-live="polite"
          >
            {result.status === 'ready'
              ? `${filteredCount} of ${total}`
              : ' '}
          </span>
        </div>
      </Card>

      {/* Content — wired by-property OR shell by-unit */}
      {view === 'units' ? (
        // BE has no Unit entity (see SCRUM-167 epic matrix). The view
        // toggle resolves to the slice's NotConnectedYet primitive
        // until the BE grows units.
        <NotConnectedYet surface="All units view" ticket="SCRUM-188" />
      ) : (
        <PropertiesListBody
          result={result}
          onRowOpen={(id) => navigate(`/properties/${id}`)}
        />
      )}
    </div>
  );
}

interface PropertiesListBodyProps {
  readonly result: ReturnType<typeof useProperties>;
  readonly onRowOpen: (id: string) => void;
}

function PropertiesListBody({
  result,
  onRowOpen,
}: PropertiesListBodyProps): React.ReactElement {
  if (result.status === 'loading') {
    return <LoadingSkeletonRows />;
  }

  if (result.status === 'forbidden') {
    return (
      <Card padding="lg" className="text-center">
        <h2
          className="text-base font-semibold mb-1"
          style={{ color: 'var(--fg-primary)' }}
        >
          You don’t have access to Properties.
        </h2>
        <p className="text-sm" style={{ color: 'var(--fg-secondary)' }}>
          Ask an administrator to grant you the property.read permission.
        </p>
      </Card>
    );
  }

  if (result.status === 'error') {
    return (
      <Card padding="lg" className="flex items-center justify-between">
        <div>
          <h2
            className="text-base font-semibold mb-1"
            style={{ color: 'var(--fg-primary)' }}
          >
            Couldn’t load properties.
          </h2>
          <p className="text-sm" style={{ color: 'var(--fg-secondary)' }}>
            Check your connection and try again.
          </p>
        </div>
        <Button variant="secondary" onClick={result.refetch}>
          Retry
        </Button>
      </Card>
    );
  }

  // ready
  const rows = result.data;
  if (rows.length === 0 && result.total === 0) {
    return <EmptyZeroProperties />;
  }
  if (rows.length === 0 && result.total > 0) {
    return <EmptyNoMatches />;
  }

  return <PropertiesTable rows={rows} onRowOpen={onRowOpen} />;
}

function EmptyZeroProperties(): React.ReactElement {
  return (
    <Card padding="lg" className="text-center">
      <h2
        className="text-base font-semibold mb-1"
        style={{ color: 'var(--fg-primary)' }}
      >
        No properties yet.
      </h2>
      <p
        className="text-sm mb-4"
        style={{ color: 'var(--fg-secondary)' }}
      >
        Create your first property to start taking reservations.
      </p>
    </Card>
  );
}

function EmptyNoMatches(): React.ReactElement {
  return (
    <Card padding="lg" className="text-center">
      <h2
        className="text-base font-semibold mb-1"
        style={{ color: 'var(--fg-primary)' }}
      >
        No matches.
      </h2>
      <p className="text-sm" style={{ color: 'var(--fg-secondary)' }}>
        Try a different search or clear filters.
      </p>
    </Card>
  );
}

function LoadingSkeletonRows(): React.ReactElement {
  // 4 skeleton rows per PROPERTIES_LIST_SPEC §7. 400ms minimum is
  // enforced by TanStack's normal request lifecycle (network always
  // > 400ms in any non-mocked environment); we don't add an artificial
  // delay here.
  return (
    <Card padding="none">
      <ul aria-busy="true" aria-live="polite">
        {[0, 1, 2, 3].map((i) => (
          <li
            key={i}
            className="flex items-center gap-3 px-4 py-3.5"
            style={{
              borderTop:
                i > 0 ? '1px solid var(--border-subtle)' : undefined,
            }}
          >
            <span
              className="inline-block rounded-md w-10 h-10"
              style={{
                background: 'var(--bg-sunken)',
              }}
              aria-hidden="true"
            />
            <span
              className="inline-block rounded w-48 h-3.5"
              style={{
                background: 'var(--bg-sunken)',
              }}
              aria-hidden="true"
            />
          </li>
        ))}
      </ul>
      <span className="sr-only">Loading properties…</span>
    </Card>
  );
}

interface PropertiesTableProps {
  readonly rows: readonly PropertyView[];
  readonly onRowOpen: (id: string) => void;
}

function PropertiesTable({
  rows,
  onRowOpen,
}: PropertiesTableProps): React.ReactElement {
  return (
    <Card padding="none">
      <table
        className="w-full text-sm"
        style={{ borderCollapse: 'collapse' }}
      >
        <thead>
          <tr
            className="text-left text-xs font-bold uppercase"
            style={{
              color: 'var(--fg-tertiary)',
              letterSpacing: '0.08em',
              background: 'var(--bg-sunken)',
            }}
          >
            <th scope="col" className="px-4 py-3">
              Property
            </th>
            <th scope="col" className="px-4 py-3">
              Status
            </th>
            <th scope="col" className="px-4 py-3 text-right">
              Photos
            </th>
            <th scope="col" className="px-4 py-3 w-10">
              <span className="sr-only">Open</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <PropertyRow
              key={row.id}
              row={row}
              onRowOpen={onRowOpen}
            />
          ))}
        </tbody>
      </table>
    </Card>
  );
}

interface PropertyRowProps {
  readonly row: PropertyView;
  readonly onRowOpen: (id: string) => void;
}

function PropertyRow({
  row,
  onRowOpen,
}: PropertyRowProps): React.ReactElement {
  const subtitle = `${row.type} · ${row.address.city}, ${row.address.stateOrProvince}`;
  return (
    <tr
      onClick={() => onRowOpen(row.id)}
      // Row keyboard navigation: Enter / Space activates the row's
      // open handler. Without `tabIndex` + key handler the row is
      // mouse-only — per ACCESSIBILITY.md §1 tab order rows must be
      // reachable via Tab.
      tabIndex={0}
      role="link"
      aria-label={`Open ${row.name}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onRowOpen(row.id);
        }
      }}
      className="cursor-pointer transition-colors"
      style={{
        borderTop: '1px solid var(--border-subtle)',
      }}
    >
      <td className="px-4 py-3.5">
        <div className="flex items-center gap-3">
          <span
            className="inline-flex items-center justify-center rounded-md text-white w-10 h-10"
            style={{
              background: 'var(--brand-500)',
            }}
            aria-hidden
          >
            <TypeIcon type={row.type} />
          </span>
          <div>
            <p
              className="m-0 text-sm font-semibold"
              style={{ color: 'var(--fg-primary)' }}
            >
              {row.name}
            </p>
            <p
              className="m-0 text-xs"
              style={{ color: 'var(--fg-secondary)' }}
            >
              {subtitle}
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3.5">
        <UnitStatus op={row.status.op} av={row.status.av} size="sm" />
      </td>
      <td
        className="px-4 py-3.5 text-right text-sm"
        style={{ color: 'var(--fg-secondary)' }}
      >
        {row.photoCount}
      </td>
      <td className="px-4 py-3.5 text-right">
        <Icon
          name="chevRight"
          size={16}
          aria-hidden
          style={{ color: 'var(--fg-tertiary)' }}
        />
      </td>
    </tr>
  );
}

// Re-export `Badge` so this file's static import isn't shaken when
// the import chain elides through tree-shaking. This is a noop in
// runtime; it keeps the kit reference for future iterations that
// surface a "filtered" badge in the filter row.
const _kitBadgeReference = Badge;
void _kitBadgeReference;

/**
 * The two-segment "By property / All units" view toggle. Lifted out
 * of the page body so the keyboard contract lives next to the role
 * declaration.
 *
 * SCRUM-167 Codex blocker 3 fix (2026-05-28): the toggle was
 * declared `role="tablist"` with `role="tab"` children but had no
 * arrow-key behaviour beyond browser-default tabbing. Per the
 * Codex review's preferred path, we KEEP the tablist semantics and
 * implement the WAI-ARIA APG §3.27 keyboard contract — ArrowLeft /
 * ArrowRight cycle, Home / End jump to the edges, and DOM focus
 * moves to the new tab so repeated arrow presses continue from the
 * right index. The pattern mirrors SectionPill's fix.
 */
function ViewToggleTablist({
  view,
  onChange,
}: {
  readonly view: ViewMode;
  readonly onChange: (next: ViewMode) => void;
}): React.ReactElement {
  const options = React.useMemo(
    () =>
      [
        { id: 'properties' as const, label: 'By property', icon: 'home' as const },
        { id: 'units' as const, label: 'All units', icon: 'grid' as const },
      ],
    [],
  );
  const tabRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    currentIndex: number,
  ): void => {
    let nextIndex: number | null = null;
    if (e.key === 'ArrowLeft') {
      nextIndex = currentIndex === 0 ? options.length - 1 : currentIndex - 1;
    } else if (e.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % options.length;
    } else if (e.key === 'Home') {
      nextIndex = 0;
    } else if (e.key === 'End') {
      nextIndex = options.length - 1;
    }
    if (nextIndex !== null) {
      e.preventDefault();
      const opt = options[nextIndex];
      if (opt !== undefined) {
        onChange(opt.id);
        const nextTab = tabRefs.current[nextIndex];
        if (nextTab !== undefined && nextTab !== null) {
          nextTab.focus();
        }
      }
    }
  };

  return (
    <div
      role="tablist"
      aria-label="Switch view"
      className="inline-flex p-0.5 mb-4 rounded-full"
      style={{ background: 'var(--bg-sunken)' }}
    >
      {options.map((opt, index) => {
        const active = opt.id === view;
        return (
          // eslint-disable-next-line @sparta/no-hand-rolled-button -- SCRUM-167: canonical WAI-ARIA tablist tab — `role="tab"` + `aria-selected` + tabIndex semantics MUST live on a native <button>; wrapping kit's <Button> would shadow the tab role.
          <button
            key={opt.id}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(opt.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold rounded-full transition-colors"
            style={{
              background: active ? 'var(--bg-surface)' : 'transparent',
              color: active ? 'var(--fg-primary)' : 'var(--fg-secondary)',
              boxShadow: active ? 'var(--shadow-sm)' : 'none',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <Icon name={opt.icon} size={14} aria-hidden />
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
